<template>
  <!--
    StepIndicator.vue - Progress-indikator for wizard
    =================================================
    Viser hvor brugeren er i sagoprettelses-flowet.

    Features:
    - Viser alle steps med numre og navne
    - Markerer det aktuelle step
    - Viser grønt flueben for gennemførte steps
    - Tillader klik på gennemførte steps for at gå tilbage
    - Springer step 5 over hvis der ikke er custom variables
  -->
  <b-card class="step-indicator-card mb-2">
    <div class="step-indicator">
      <div
        v-for="(step, index) in visibleSteps"
        :key="step.id"
        class="step-item"
        :class="{
          'step-active': step.id === currentStep,
          'step-completed': step.id < currentStep,
          'step-clickable': step.id <= currentStep,
        }"
        @click="handleStepClick(step.id)"
      >
        <!-- Step cirkel med nummer eller flueben -->
        <div class="step-circle">
          <feather-icon
            v-if="step.id < currentStep"
            icon="CheckIcon"
            size="16"
          />
          <span v-else>{{ getDisplayNumber(index) }}</span>
        </div>

        <!-- Step navn -->
        <div class="step-label">
          {{ step.name }}
        </div>

        <!-- Forbindelseslinje til næste step -->
        <div
          v-if="index < visibleSteps.length - 1"
          class="step-line"
          :class="{ 'step-line-completed': step.id < currentStep }"
        />
      </div>
    </div>
  </b-card>
</template>

<script>
/**
 * StepIndicator Script
 *
 * Props:
 * - steps: Array af step-objekter fra STEPS konstant
 * - currentStep: Det aktuelle step-nummer (1-6)
 * - hasCustomVariables: Om step 5 skal vises
 *
 * Events:
 * - step-click: Emittet når brugeren klikker på et step
 */
import { BCard } from 'bootstrap-vue'
import { computed } from '@vue/composition-api'

export default {
  name: 'StepIndicator',

  components: {
    BCard,
  },

  props: {
    steps: {
      type: Array,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    hasCustomVariables: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    /**
     * Filtrerer steps så step 5 (custom variables) kun vises
     * hvis der faktisk er custom variables at udfylde.
     */
    const visibleSteps = computed(() => props.steps.filter(step => {
      // Skjul step 3 (custom variables) hvis der ikke er påkrævede custom variables
      if (step.id === 3) return props.hasCustomVariables
      return true
    }))

    /**
     * Beregner det viste nummer for et step.
     * Hvis step 5 er skjult, skal step 6 vises som "5".
     */
    function getDisplayNumber(index) {
      return index + 1
    }

    /**
     * Håndterer klik på et step.
     * Tillader kun at klikke på gennemførte steps eller det aktuelle.
     */
    function handleStepClick(stepId) {
      if (stepId <= props.currentStep) {
        emit('step-click', stepId)
      }
    }

    return {
      visibleSteps,
      getDisplayNumber,
      handleStepClick,
    }
  },
}
</script>

<style lang="scss" scoped>
/**
 * Step Indicator Styles
 *
 * Designet matcher Debtia's farveskema med primærfarve #088c76 (grøn)
 * og #002954 (blå) for gennemførte steps.
 */

// Debtia farver
$debtia-green: #088c76;
$debtia-blue: #002954;
$debtia-success: #28c76f;

.step-indicator-card {
  // Fjern standard padding fra card
  .card-body {
    padding: 1rem;
  }
}

.step-indicator {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  cursor: default;

  // Gør gennemførte steps klikbare
  &.step-clickable {
    cursor: pointer;

    &:hover .step-circle {
      transform: scale(1.1);
    }
  }
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.2s ease;
  z-index: 2;
  position: relative;

  // Default (ikke aktiv, ikke gennemført)
  background-color: #e9ecef;
  color: #6c757d;
  border: 2px solid #e9ecef;

  // Aktiv step - Debtia grøn
  .step-active & {
    background-color: $debtia-green;
    color: white;
    border-color: $debtia-green;
    box-shadow: 0 0 0 4px rgba(8, 140, 118, 0.2);
  }

  // Gennemført step - Debtia blå
  .step-completed & {
    background-color: $debtia-blue;
    color: white;
    border-color: $debtia-blue;
  }
}

.step-label {
  margin-top: 8px;
  font-size: 12px;
  color: #6c757d;
  text-align: center;
  white-space: nowrap;

  // Aktiv step label - Debtia grøn
  .step-active & {
    color: $debtia-green;
    font-weight: 700;
  }

  // Gennemført step label - Debtia blå
  .step-completed & {
    color: $debtia-blue;
  }
}

.step-line {
  position: absolute;
  top: 20px; // Midt i cirklen
  left: 50%;
  width: 100%;
  height: 2px;
  background-color: #e9ecef;
  z-index: 1;

  // Gennemført linje - Debtia blå
  &.step-line-completed {
    background-color: $debtia-blue;
  }
}

// Responsiv: Stack på små skærme
@media (max-width: 768px) {
  .step-indicator {
    flex-wrap: wrap;
    gap: 1rem;
  }

  .step-item {
    flex: 0 0 auto;
    min-width: 60px;
  }

  .step-line {
    display: none;
  }

  .step-label {
    font-size: 10px;
  }
}
</style>
